import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, ListGroup, Badge, Spinner } from "react-bootstrap";
import Sample14 from "../Components/Logo";
import { useAuth } from "../Context/AuthContext";
import { fetchCompnayCodesById } from "../Api/InvoiceApi";
import { Base_URL } from "../url";
import axios from "axios";
import { toast } from "react-toastify";

// ProductionProcessScreen
// - Fields: Process Name, OrgId (prefill), Company Code (dropdown)
// - Loads stations for a dropdown, then loads subprocesses via GetSubprocess API
// - Displays available subprocesses (todo-list) with + and - buttons
// - Sends payload to POST ${Base_URL}/ProductionStation/CreateProcess

const ProductionProcess = () => {
  const { user } = useAuth();
  const orgId = user?.organisationId;

  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [companyCode, setCompanyCode] = useState("");

  const [stations, setStations] = useState([]);
  const [selectedStationId, setSelectedStationId] = useState("");

  const [availableSubProcesses, setAvailableSubProcesses] = useState([]);
  const [selectedSubProcessIds, setSelectedSubProcessIds] = useState([]);

  const [processName, setProcessName] = useState("");

  useEffect(() => {
    const loadLookups = async () => {
      setDropdownLoading(true);
      try {
        const comps = await fetchCompnayCodesById(orgId);
        setCompanyCodes(comps || []);
        if (comps && comps.length > 0) {
          const first = comps[0];
          const code = first.companyCode ?? first.code ?? first.value ?? first.id ?? first.CompanyCode;
          setCompanyCode(String(code));
        }

        // load stations for dropdown
        const res = await fetch(`${Base_URL}/ProductionStation/id?id=${encodeURIComponent(orgId || "")}`);
        if (res.ok) {
          const data = await res.json();
          const arr = Array.isArray(data) ? data : [];

          // unique by productionStationId/StationId/id
          const unique = arr.filter((it, idx, self) => {
            const key = it.productionStationId ?? it.StationId ?? it.id ?? it.stationId;
            return idx === self.findIndex(s => (s.productionStationId ?? s.StationId ?? s.id ?? s.stationId) === key);
          });

          setStations(unique);

          if (unique.length > 0) {
            const firstId = unique[0].productionStationId ?? unique[0].StationId ?? unique[0].id ?? unique[0].stationId ?? "";
            setSelectedStationId(String(firstId));
          }
        }
      } catch (err) {
        console.error("Error loading lookups", err);
        toast.error("Error loading lookup data");
      } finally {
        setDropdownLoading(false);
      }
    };

    loadLookups();
  }, [orgId]);

  const loadSubProcesses = async (stationId) => {
    if (!stationId) return;
    setLoading(true);
    try {
      const res = await fetch(`${Base_URL}/ProductionStation/GetSubprocess/id?id=${encodeURIComponent(stationId)}`);
      if (!res.ok) throw new Error(`Failed to load subprocesses: ${res.status}`);
      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      const normalized = arr.map((it, i) => {
        const id = it.productionSubprocessId ?? it.subProcessId ?? it.id ?? `sp-${i}`;
        const name = it.productionSubprocessDesc ?? it.processName ?? it.name ?? `SubProcess ${i + 1}`;
        return { id: String(id), name, raw: it };
      });
      setAvailableSubProcesses(normalized);
      // NOTE: Do NOT clear selectedSubProcessIds here — keep user's selections even when loading another station
    } catch (err) {
      console.error(err);
      setAvailableSubProcesses([]);
      toast.error("Error loading subprocesses: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // when station dropdown changes, clear available list but keep selected processes
  const onStationSelect = (val) => {
    setSelectedStationId(val);
    setAvailableSubProcesses([]); // keep selectedSubProcessIds intact
    // DO NOT clear selectedSubProcessIds so user selection remains across stations
  };

  const handleAdd = (id) => setSelectedSubProcessIds((p) => (p.includes(id) ? p : [...p, id]));
  const handleRemove = (id) => setSelectedSubProcessIds((p) => p.filter(x => x !== id));

  const handleSave = async () => {
    if (!companyCode) return toast.error("Select Company Code");
    if (!processName) return toast.error("Enter Process Name");
    if (selectedSubProcessIds.length === 0) return toast.error("Select at least one subprocess");

    const selected = availableSubProcesses.filter(s => selectedSubProcessIds.includes(s.id))
      // also include selected that may not be in current availableSubProcesses (previously loaded)
      .concat(
        // find any selected ids that aren't in current available list by searching stations array raw data if possible
        selectedSubProcessIds
          .filter(id => !availableSubProcesses.some(s => s.id === id))
          .map(id => ({ id, name: id }))
      );

    const payload = {
      orgId,
      companyCode: Number(companyCode),
      processName: processName || null,
      subProcesses: selected.map(s => ({ id: s.id, name: s.name })),
      createdBy: user?.userId ?? user?.organisationId ?? null,
    };
    console.log(payload);
    setLoading(true);
    try {
      const res = await axios.post(`${Base_URL}/ProductionStation/CreateProcess`, payload);

      if (res.status === 200 || res.status === 201) {
        toast.success("Created Successfully");
        // reset UI
        setProcessName("");
        setSelectedSubProcessIds([]);
        setAvailableSubProcesses([]);
      } else {
        console.error("CreateProcess response:", res);
        toast.error("Error creating process");
      }
    } catch (err) {
      console.error("Error creating process:", err);
      const msg = err?.response?.data?.message ?? err?.message ?? "Error creating process";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h3 className="fields-title">Production Process</h3>

        <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="fields-form">
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Process Name</Form.Label>
                <Form.Control value={processName} onChange={(e) => setProcessName(e.target.value)} />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>OrgId</Form.Label>
                <Form.Control value={String(orgId ?? "")} readOnly />
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>Company Code</Form.Label>
                <Form.Control as="select" value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} disabled={dropdownLoading}>
                  <option value="">Select Company Code</option>
                  {companyCodes.map((c, i) => {
                    const val = c.companyCode ?? c.code ?? c.value ?? c.id ?? c.CompanyCode;
                    const label = c.name ?? c.companyName ?? c.label ?? String(val);
                    return <option key={i} value={String(val)}>{`${val} - ${label}`}</option>;
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Row className="align-items-end">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Production Station</Form.Label>
                <Form.Control as="select" value={selectedStationId} onChange={(e) => onStationSelect(e.target.value)} disabled={dropdownLoading}>
                  <option value="">Select Station</option>
                  {stations.map((s, i) => {
                    const id = s.productionStationId ?? s.StationId ?? s.id ?? s.stationId ?? "";
                    const label = s.StationName ?? s.stationName ?? s.name ?? s.Name ?? s.productionStationDesc ??String(id);
                    return <option key={i} value={String(id)}>{label}</option>;
                  })}
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Button type="button" onClick={() => loadSubProcesses(selectedStationId)} disabled={loading || !selectedStationId} className="mb-3">
                {loading ? <Spinner animation="border" size="sm" /> : 'Load'}
              </Button>
            </Col>

            <Col md={4} className="text-end">
              <div style={{ marginBottom: 8 }}>
                <Badge bg="secondary">Available: {availableSubProcesses.length}</Badge>{' '}
                <Badge bg="primary">Selected: {selectedSubProcessIds.length}</Badge>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <h5>Available Sub-Processes</h5>
              <ListGroup>
                {availableSubProcesses.map((sp) => (
                  <ListGroup.Item key={sp.id} className="d-flex justify-content-between align-items-center">
                    <div>
                      <div style={{ fontSize: 12, color: '#666' }}> {sp.name}-{sp.id}</div>
                    </div>
                    <div>
                      {!selectedSubProcessIds.includes(sp.id) ? (
                        <Button size="sm" onClick={() => handleAdd(sp.id)}>+</Button>
                      ) : (
                        <Button variant="danger" size="sm" onClick={() => handleRemove(sp.id)}>-</Button>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
                {availableSubProcesses.length === 0 && <ListGroup.Item>No subprocesses loaded</ListGroup.Item>}
              </ListGroup>
            </Col>

            <Col md={6}>
              <h5>Selected Sub-Processes (Payload)</h5>
              <ListGroup>
                {selectedSubProcessIds.length === 0 && <ListGroup.Item>No subprocesses selected</ListGroup.Item>}
                {selectedSubProcessIds.map((id) => {
                  const sp = availableSubProcesses.find((s) => s.id === id);
                  return (
                    <ListGroup.Item key={id} className="d-flex justify-content-between align-items-center">
                      <div>
                        <div style={{ fontSize: 12, color: '#666' }}>{sp?.name ?? id}-{id}</div>
                      </div>
                      <div>
                        <Button variant="outline-danger" size="sm" onClick={() => handleRemove(id)}>-</Button>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-3">
            <Button type="submit" disabled={loading || dropdownLoading}>
              {loading ? (<><Spinner animation="border" size="sm" /> Saving...</>) : ("Save Process")}
            </Button>

            <Button variant="secondary" type="button" onClick={() => {
              setProcessName("");
              setSelectedSubProcessIds([]);
            }}>Clear</Button>
          </div>
        </Form>
      </Container>

      {(loading || dropdownLoading) && (
        <div className="loading-overlay"><Sample14 /></div>
      )}
    </div>
  );
};

export default ProductionProcess;
